import { useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { TopBar } from './TopBar'
import { Sidebar } from './Sidebar'
import { SearchDialog } from './SearchDialog'
import { SignInDialog } from './SignIn'
import { Footer } from './Footer'
import { cx } from './ui'

/**
 * Signed in, but the backend would not tell us a role — a deactivated account, a
 * revoked user, a clock-skewed token. Reads fall back to the anonymous view, which
 * is silently confusing unless we say so.
 */
function AccountBanner() {
  const { user, loading, role, error, signOut } = useAuth()
  if (loading || !user || role) return null

  return (
    <div className="border-b border-line bg-internal-soft px-4 py-2 text-center text-sm text-ink sm:px-6">
      Your account could not be verified{error ? `: ${error}` : ''} — you are browsing with
      public access only.{' '}
      <button onClick={() => void signOut()} className="font-medium underline">
        Sign out
      </button>
    </div>
  )
}

export function Layout() {
  const [searchOpen, setSearchOpen] = useState(false)
  const [navOpen, setNavOpen] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setSearchOpen((o) => !o)
      }
      // Bare "/" opens search, unless the reader is already typing somewhere.
      const target = e.target as HTMLElement | null
      const typing = target?.matches('input, textarea, [contenteditable]')
      if (e.key === '/' && !typing) {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div className="min-h-dvh bg-canvas">
      <TopBar onOpenSearch={() => setSearchOpen(true)} onToggleNav={() => setNavOpen((o) => !o)} />
      <AccountBanner />

      <div className="mx-auto flex max-w-[1600px] px-4 sm:px-6">
        <aside
          className={cx(
            // Doc titles are route signatures, not words — the rail needs room.
            'scroll-slim w-72 shrink-0 overflow-y-auto',
            'sticky top-16 h-[calc(100dvh-4rem)]',
            'max-lg:fixed max-lg:top-16 max-lg:left-0 max-lg:z-30 max-lg:w-72 max-lg:border-r max-lg:border-line max-lg:bg-surface max-lg:px-4',
            navOpen ? 'max-lg:block' : 'max-lg:hidden',
          )}
        >
          <Sidebar onNavigate={() => setNavOpen(false)} />
        </aside>

        {navOpen && (
          <div
            className="fixed inset-0 top-16 z-20 bg-ink/30 lg:hidden"
            onClick={() => setNavOpen(false)}
            role="presentation"
          />
        )}

        <main className="min-w-0 flex-1 pb-16">
          <Outlet />
        </main>
      </div>

      <Footer />

      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
      <SignInDialog />
    </div>
  )
}
